// Full leaderboard: one side at a time with every number, ranked models first, then the ones
// still short of the match minimum. Every setup and mode counts unless the reader narrows it.

import type { LeaderboardResponse, LeaderboardRow, ModeFilter, SetupFilter } from '../src/api/contract';
import type { Side } from '../src/types';
import { api, describeError, isAbort, modelUrl } from './api';
import { emptyState, errorState, getParam, h, link, loadingState, replaceQuery, requireEl, show } from './dom';
import { onRadio, setRadio } from './filters';
import { MIN_RANKED_MATCHES, glossary } from './help';
import { MODE_HINT, SETUP_HINT, fmtAgo, isMode, isModeFilter, isSetup, isSetupFilter, modeLabel, setupLabel } from './format';
import { isRanked, metricColumns, metricHelp, rankBadge, rankOrder, ranks } from './metrics';
import { sortableTable, type Column } from './table';
import { egoColumns, isRefuser, modelCell, refusersList } from './uprising';

interface State {
  role: Side;
  mode: ModeFilter;
  setup: SetupFilter;
}

export async function initLeaderboard(): Promise<void> {
  const tableBox = requireEl('lb-table');
  const note = requireEl('lb-note');
  const roleGroup = requireEl('lb-role');
  const setupSel = requireEl<HTMLSelectElement>('lb-setup');
  const modeSel = requireEl<HTMLSelectElement>('lb-mode');
  const sliceHint = requireEl('lb-slice-hint');
  const clear = requireEl<HTMLButtonElement>('lb-clear');
  const help = requireEl('lb-help');

  const setupParam = getParam('setup');
  const modeParam = getParam('mode');
  const state: State = {
    role: getParam('role') === 'attacker' ? 'attacker' : 'defender',
    mode: isModeFilter(modeParam) ? modeParam : 'all',
    setup: isSetupFilter(setupParam) ? setupParam : 'all',
  };
  setRadio(roleGroup, 'role', state.role);
  setupSel.value = state.setup;
  modeSel.value = state.mode;

  const syncUrl = (): void =>
    replaceQuery({
      role: state.role === 'defender' ? null : state.role,
      mode: state.mode === 'all' ? null : state.mode,
      setup: state.setup === 'all' ? null : state.setup,
    });

  const syncSlice = (): void => {
    const lines: string[] = [];
    if (isSetup(state.setup)) lines.push(`${setupLabel(state.setup)}: ${SETUP_HINT[state.setup]}.`);
    if (isMode(state.mode)) lines.push(`${modeLabel(state.mode)} defender: ${MODE_HINT[state.mode]}.`);
    sliceHint.textContent = lines.length ? lines.join(' ') : 'Every match counts: all four setups and all three kinds of defender instructions.';
    clear.hidden = state.setup === 'all' && state.mode === 'all';
  };

  let ctrl: AbortController | null = null;

  const loadTable = async (): Promise<void> => {
    ctrl?.abort();
    const myCtrl = new AbortController();
    ctrl = myCtrl;
    tableBox.setAttribute('aria-busy', 'true');
    show(tableBox, loadingState('Loading leaderboard'));
    show(help, glossary([...metricHelp(state.role), ...(state.setup === 'uprising' ? egoColumns(state.role).flatMap((c) => (c.help ? [c.help] : [])) : [])]));
    try {
      const res = await api.leaderboard({ role: state.role, mode: state.mode, setup: state.setup }, myCtrl.signal);
      renderTable(tableBox, note, res, state);
    } catch (err) {
      if (isAbort(err) || myCtrl.signal.aborted) return;
      note.textContent = '';
      show(tableBox, errorState(describeError(err), () => void loadTable()));
    } finally {
      if (ctrl === myCtrl) tableBox.removeAttribute('aria-busy');
    }
  };

  const changed = (): void => {
    syncUrl();
    syncSlice();
    void loadTable();
  };
  onRadio(roleGroup, 'role', (v) => {
    state.role = v === 'attacker' ? 'attacker' : 'defender';
    changed();
  });
  setupSel.addEventListener('change', () => {
    state.setup = isSetupFilter(setupSel.value) ? setupSel.value : 'all';
    changed();
  });
  modeSel.addEventListener('change', () => {
    state.mode = isModeFilter(modeSel.value) ? modeSel.value : 'all';
    changed();
  });
  clear.addEventListener('click', () => {
    state.setup = 'all';
    state.mode = 'all';
    setupSel.value = 'all';
    modeSel.value = 'all';
    changed();
  });
  syncSlice();
  await loadTable();
}

function modelColumn(role: Side): Column<LeaderboardRow> {
  return {
    key: 'model',
    label: 'Model',
    cls: 'col-model',
    value: (r) => r.displayName || r.modelId,
    render: (r) => modelCell(r, role === 'attacker'),
  };
}

function renderTable(box: HTMLElement, note: HTMLElement, res: LeaderboardResponse, state: State): void {
  const played = (Array.isArray(res.rows) ? res.rows : []).filter((r) => r && typeof r.modelId === 'string' && r.matches > 0);
  const role: Side = res.role === 'attacker' || res.role === 'defender' ? res.role : state.role;
  // Attackers that mostly refuse to play are listed apart, not ranked at the bottom.
  const refusers = role === 'attacker' ? refusersList(played, state.setup === 'all' ? 'as the attacker' : 'as the attacker in this setup') : null;
  const rows = role === 'attacker' ? played.filter((r) => !isRefuser(r)) : played;
  const side = role === 'attacker' ? 'attacker' : 'defender';
  note.textContent = res.generatedAt ? `Updated ${fmtAgo(res.generatedAt)}.` : '';
  if (rows.length === 0 && !refusers) {
    const sliced = state.setup !== 'all' || state.mode !== 'all';
    show(
      box,
      emptyState(
        'No matches yet',
        sliced ? 'No counted matches in this setup and mode yet. Clear the filter to see every match.' : 'Rankings appear here as soon as the first matches finish.',
      ),
    );
    return;
  }

  const ranked = rows.filter(isRanked);
  const waiting = rows.filter((r) => !isRanked(r));
  const rank = ranks(ranked, role);
  // The Uprising slice adds its ego columns.
  const metrics = [...metricColumns<LeaderboardRow>(role), ...(state.setup === 'uprising' ? egoColumns(role) : [])];
  const parts: Node[] = [];

  if (ranked.length > 0) {
    parts.push(
      sortableTable({
        caption: `${role === 'attacker' ? 'Attack' : 'Defense'} ranking`,
        columns: [
          {
            key: 'rank',
            label: '#',
            numeric: true,
            firstDir: 'asc',
            value: (r) => rank.get(r) ?? null,
            render: (r) => rankBadge(rank.get(r) ?? null),
          },
          modelColumn(role),
          ...metrics,
        ],
        rows: ranked,
        defaultOrder: rankOrder<LeaderboardRow>(role),
        initialSort: { key: 'rank', dir: 'asc' },
        rowHeaderKey: 'model',
      }),
    );
  } else {
    parts.push(emptyState('No ranked models yet', `A model is ranked once it has played ${MIN_RANKED_MATCHES} counted matches as the ${side}.`));
  }

  if (waiting.length > 0) {
    parts.push(
      h(
        'section',
        { class: 'unranked', attrs: { 'aria-labelledby': 'unranked-title' } },
        h('h2', { attrs: { id: 'unranked-title' } }, 'Not ranked yet'),
        h(
          'p',
          { class: 'hint' },
          `Fewer than ${MIN_RANKED_MATCHES} matches as the ${side}, so their averages still swing a lot from one match to the next.`,
        ),
        sortableTable({
          caption: `${role === 'attacker' ? 'Attackers' : 'Defenders'} with fewer than ${MIN_RANKED_MATCHES} matches`,
          columns: [modelColumn(role), ...metrics],
          rows: waiting,
          defaultOrder: (a, b) => b.matches - a.matches || rankOrder<LeaderboardRow>(role)(a, b),
          rowHeaderKey: 'model',
        }),
      ),
    );
  }
  if (refusers) parts.push(refusers);
  show(box, ...parts);
}
